import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Card, EmptyState, Field } from "../components/ui-ext";
import { Plus, X, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function UsersPage() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [resetting, setResetting] = useState(null);

  const load = () => api.get("/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader
        eyebrow="Team"
        title="Users"
        subtitle="Manage engineers, managers and admins."
        right={<button onClick={() => setShow(true)} className="bg-[#FF5722] hover:bg-[#F4511E] px-4 py-2 text-sm rounded-sm flex items-center gap-2" data-testid="new-user-btn"><Plus className="w-4 h-4" /> New User</button>}
      />
      <Card>
        {items.length === 0 ? <EmptyState title="No users" /> : (
          <div className="overflow-x-auto">
            <table className="w-full tech-table">
              <thead><tr><th>Name</th><th>Email</th><th>Emp ID</th><th>Role</th><th>Mobile</th><th>Active</th><th>Action</th></tr></thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.id}>
                    <td className="font-medium">{u.name}</td>
                    <td className="mono text-xs">{u.email}</td>
                    <td className="mono">{u.emp_id || "—"}</td>
                    <td><span className={`mono text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm ${u.role === "admin" ? "border-[#FF5722] text-[#FF5722]" : u.role === "manager" ? "border-blue-400 text-blue-400" : "border-white/20 text-slate-300"}`}>{u.role}</span></td>
                    <td className="mono text-xs">{u.mobile || "—"}</td>
                    <td>{u.active ? <span className="text-green-400 mono text-xs">ACTIVE</span> : <span className="text-slate-500 mono text-xs">INACTIVE</span>}</td>
                    <td>
                      <button onClick={() => setResetting(u)} className="border border-white/15 hover:border-[#FF5722] text-xs px-2 py-1 mono uppercase flex items-center gap-1" data-testid={`reset-pw-${u.id}`}>
                        <KeyRound className="w-3 h-3" /> Reset
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {show && <UserDialog onClose={() => setShow(false)} onCreated={() => { setShow(false); load(); }} />}
      {resetting && <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />}
    </div>
  );
}

function UserDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "", password: "", name: "", role: "engineer",
    emp_id: "", mobile: "", dob: "", address: "", aadhaar: "",
    bank_account: "", ifsc: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.email || !form.password || !form.name) return toast.error("Fill required fields");
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      onCreated();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#13151A] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-white/10 flex justify-between sticky top-0 bg-[#13151A]">
          <h2 className="text-lg font-semibold">Create User</h2>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 grid grid-cols-2 gap-4">
          <Field label="Name *"><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="u-name" /></Field>
          <Field label="Role">
            <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="u-role">
              <option value="engineer">Engineer</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <Field label="Email *"><input type="email" className="input mono" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="u-email" /></Field>
          <Field label="Password *"><input type="text" className="input mono" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="u-password" /></Field>
          <Field label="Emp ID"><input className="input mono" value={form.emp_id} onChange={(e) => setForm({ ...form, emp_id: e.target.value })} /></Field>
          <Field label="Mobile"><input className="input mono" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></Field>
          <Field label="DOB"><input type="date" className="input" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} /></Field>
          <Field label="Aadhaar"><input className="input mono" value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Address"><textarea rows={2} className="input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field></div>
          <Field label="Bank A/c"><input className="input mono" value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} /></Field>
          <Field label="IFSC"><input className="input mono" value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value })} /></Field>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="border border-white/15 px-4 py-2 text-sm rounded-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="bg-[#FF5722] hover:bg-[#F4511E] px-5 py-2 text-sm rounded-sm flex items-center gap-2" data-testid="u-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Create</button>
        </div>
        <style>{`.input { width: 100%; background: #0B0C10; border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; font-size: 13px; border-radius: 2px; color: white; }`}</style>
      </div>
    </div>
  );
}

function ResetPasswordDialog({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (pw.length < 6) return toast.error("Min 6 chars");
    setBusy(true);
    try {
      await api.post(`/users/${user.id}/reset-password`, { new_password: pw });
      toast.success("Password reset");
      onClose();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#13151A] border border-white/10 w-full max-w-sm">
        <div className="px-6 py-4 border-b border-white/10">
          <div className="text-lg font-semibold">Reset Password</div>
          <div className="text-xs text-slate-400 mt-1">For <span className="mono">{user.email}</span></div>
        </div>
        <div className="p-6">
          <Field label="New Password"><input type="text" className="input mono" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw-input" /></Field>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="border border-white/15 px-4 py-2 text-sm rounded-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="bg-[#FF5722] hover:bg-[#F4511E] px-5 py-2 text-sm rounded-sm" data-testid="reset-pw-submit">Reset</button>
        </div>
        <style>{`.input { width: 100%; background: #0B0C10; border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; font-size: 13px; border-radius: 2px; color: white; }`}</style>
      </div>
    </div>
  );
}
